import React, { useState, useEffect } from 'react';
import type { Animal } from '../data/types';
import './AddAnimalModal.css';
import './EditAnimalModal.css';

interface EditAnimalModalProps {
    animal: Animal | null;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (animal: Animal) => void;
}

export function EditAnimalModal({ animal, isOpen, onClose, onSubmit }: EditAnimalModalProps) {
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [feedingInterval, setFeedingInterval] = useState('2');

    useEffect(() => {
        if (animal) {
            setName(animal.name);
            setNotes(animal.notes || '');
            setFeedingInterval(animal.feedingIntervalHours.toString());
        }
    }, [animal]);

    const handleSubmit = () => {
        if (!animal) return;

        if (!name.trim()) {
            alert('Please enter a name');
            return;
        }

        const interval = parseFloat(feedingInterval);
        if (!interval || interval <= 0) {
            alert('Please enter a valid feeding interval');
            return;
        }

        const updatedAnimal: Animal = {
            ...animal,
            name: name.trim(),
            notes: notes.trim() || undefined,
            feedingIntervalHours: interval,
            updatedAt: Date.now(),
        };

        onSubmit(updatedAnimal);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen || !animal) return null;

    return (
        <div className="modal active edit-modal" onClick={handleBackdropClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Edit {animal.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                        type="text"
                        className="form-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Mochi"
                        autoFocus
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Feeding Interval <span className="unit">(hours)</span>
                    </label>
                    <div className="interval-presets">
                        {[1, 2, 3, 4, 6, 8].map((hours) => (
                            <div
                                key={hours}
                                className={`interval-option ${feedingInterval === hours.toString() ? 'selected' : ''}`}
                                onClick={() => setFeedingInterval(hours.toString())}
                            >
                                {hours}h
                            </div>
                        ))}
                    </div>
                    <input
                        type="number"
                        className="form-input"
                        value={feedingInterval}
                        onChange={(e) => setFeedingInterval(e.target.value)}
                        placeholder="Custom interval"
                        step="0.5"
                        min="0.5"
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Notes <span className="unit">(optional)</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Age, intake notes, special needs..."
                        rows={3}
                    />
                </div>

                <button className="submit-btn" onClick={handleSubmit}>
                    Save Changes
                </button>
            </div>
        </div>
    );
}
